import { Link } from 'react-router-dom';

import {
  ReferralProgramConfigurationFragment,
  ReferralProgramStatisticsFragment,
  SubBrand,
} from '@occc/graphql';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { AwardType } from '@occc-app/offerHelpers/types';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { Button, EditIcon, VisibilityOutlinedIcon } from '@occc-app/design-system';
import { parseDate } from '@occc-app/lib/date';

import {
  ConfigurationDetailsAward,
  ConfigurationDetailsMarketingContent,
  ConfigurationDetailsQualifyingEvent,
  ConfigurationDetailsProps,
  MetricsValues,
} from './components';

import { routeCreator } from '../../referral-program.router';
import { mapMarketingContentsFromConfiguration } from '../referral-program-configuration-page/referral-program-configuration.utils';

const mapAwardToConfigurationDetailsAward = (
  award:
    | ReferralProgramConfigurationFragment['senderConfiguration']['award']
    | ReferralProgramConfigurationFragment['receiverConfiguration']['award'],
): ConfigurationDetailsAward => {
  if (award?.__typename === 'AwardPoints') {
    return {
      type: AwardType.AwardPoints,
      points: award.points,
    };
  }
  if (award?.__typename === 'AwardVouchers') {
    return {
      type: AwardType.AwardVouchers,
      voucheredOffer: { id: award.voucheredOffer.id, name: award.voucheredOffer.name },
    };
  }
  return { type: AwardType.AwardNullable };
};

const mapQualifyingEventToConfigurationDetailsQualifyingEvent = (
  qualifyingEvent: ReferralProgramConfigurationFragment['qualifiedEvent'],
): ConfigurationDetailsQualifyingEvent => {
  return qualifyingEvent.__typename === 'QualifiedEventTransaction'
    ? {
        type: 'transaction',
        productGroups: qualifyingEvent.eligibleProductGroups.map((productGroup) => ({
          id: productGroup.id,
          name: productGroup.name,
        })),
        threshold: qualifyingEvent.purchaseThreshold,
        countProductsBy: qualifyingEvent.countProductsBy,
      }
    : {
        type: 'loyaltyEvent',
        loyaltyEventOffer: {
          id: qualifyingEvent.loyaltyEventOffer.id,
          name: qualifyingEvent.loyaltyEventOffer.name,
        },
      };
};

const mapMarketingContentsForDetails = (
  referralProgram: ReferralProgramConfigurationFragment,
  subBrands: SubBrand[] | null,
): ConfigurationDetailsMarketingContent[] => {
  const marketingContents = mapMarketingContentsFromConfiguration({
    referralProgramConfiguration: referralProgram,
    subBrands,
  });

  if (subBrands?.length) {
    return subBrands.map((subBrand) => {
      const content = marketingContents.find((item) => item.subBrandUuid === subBrand.uuid);

      return {
        subBrandName: subBrand.name,
        senderInstructions: content?.senderInstructions ?? '',
        receiverInstructions: content?.receiverInstructions ?? '',
        smsTemplate: content?.smsTemplate ?? '',
      };
    });
  }

  const content =
    marketingContents.find((item) => item.subBrandUuid == null) ?? marketingContents[0];

  return [
    {
      senderInstructions: content?.senderInstructions ?? '',
      receiverInstructions: content?.receiverInstructions ?? '',
      smsTemplate: content?.smsTemplate ?? '',
    },
  ];
};

export const mapReferralProgramToConfigurationDetailsProps = (
  referralProgram: ReferralProgramConfigurationFragment,
  subBrands: SubBrand[] | null = null,
): ConfigurationDetailsProps => {
  const { senderConfiguration, receiverConfiguration, qualifiedEvent } = referralProgram;
  return {
    senderAward: mapAwardToConfigurationDetailsAward(senderConfiguration.award),
    receiverAward: mapAwardToConfigurationDetailsAward(receiverConfiguration.award),
    qualifyingEvent: mapQualifyingEventToConfigurationDetailsQualifyingEvent(qualifiedEvent),
    marketingContents: mapMarketingContentsForDetails(referralProgram, subBrands),
  };
};

export const mapReferralProgramStatisticsToMetricsValues = (
  statistics: ReferralProgramStatisticsFragment | null,
): MetricsValues => {
  if (!statistics) {
    return {
      uniqueCodesShared: 0,
      newMembersReferred: 0,
      newMembersPerReferral: 0,
      completedReferrals: 0,
      senderAwardsGranted: 0,
      senderAwardsRedeemed: 0,
      receiverAwardsGranted: 0,
      receiverAwardsRedeemed: 0,
      senderPointsGranted: 0,
      receiverPointsGranted: 0,
      chartData: [],
    };
  }

  return {
    uniqueCodesShared: statistics.uniqueCodesShared,
    newMembersReferred: statistics.newMembersReferred,
    newMembersPerReferral: statistics.newMembersPerReferral,
    completedReferrals: statistics.completedReferrals,
    senderAwardsGranted: statistics.senderAwardGranted,
    senderAwardsRedeemed: statistics.senderAwardRedeemed,
    receiverAwardsGranted: statistics.receiverAwardGranted,
    receiverAwardsRedeemed: statistics.receiverAwardRedeemed,
    senderPointsGranted: statistics.senderPointsGranted,
    receiverPointsGranted: statistics.receiverPointsGranted,
    chartData: statistics.timeSeries.map((item) => ({
      date: parseDate(item.date),
      uniqueCodesShared: item.uniqueCodesSharedCount,
      newMembersReferred: item.newMembersReferredCount,
    })),
  };
};

export const getReferralProgramPageActions = (params: {
  referralProgramConfiguration: ReferralProgramConfigurationFragment | null;
  isUserPermittedToEdit: boolean;
  isUserPermittedToView: boolean;
}) => {
  const { referralProgramConfiguration, isUserPermittedToEdit, isUserPermittedToView } = params;
  if (!referralProgramConfiguration) return null;

  if (isUserPermittedToEdit) {
    return (
      <Button
        key='edit'
        color='secondary'
        variant='contained'
        component={Link}
        to={routeCreator.configure()}
        startIcon={<EditIcon />}
        role='button'
      >
        Edit
      </Button>
    );
  }

  if (isUserPermittedToView) {
    return (
      <Button
        key='edit'
        color='secondary'
        variant='contained'
        component={Link}
        to={routeCreator.configure()}
        startIcon={<VisibilityOutlinedIcon />}
        role='button'
      >
        View configuration
      </Button>
    );
  }

  return null;
};
